import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { PhotoMarker } from '../types/types';

export type MarkerAutosaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';

interface UseMarkerAutosaveOptions {
  active: boolean;
  enabled: boolean;
  layoutId: string;
  markers: PhotoMarker[];
  debounceMs?: number;
  save: (layoutId: string, markers: PhotoMarker[]) => Promise<void>;
  onError?: (error: unknown) => void;
}

export const getMarkerDraftFingerprint = (markers: readonly PhotoMarker[]): string =>
  JSON.stringify(
    markers.map((marker) => ({
      id: marker.id,
      dbId: marker.dbId ?? null,
      number: marker.number,
      x: marker.x,
      y: marker.y,
      width: marker.width,
      height: marker.height,
      fontSize: marker.fontSize,
    }))
  );

export const useMarkerAutosave = ({
  active,
  enabled,
  layoutId,
  markers,
  debounceMs = 1500,
  save,
  onError,
}: UseMarkerAutosaveOptions) => {
  const fingerprint = useMemo(() => getMarkerDraftFingerprint(markers), [markers]);
  const latestRef = useRef({ layoutId, markers, fingerprint });
  const saveRef = useRef(save);
  const onErrorRef = useRef(onError);
  const sessionRef = useRef<string | null>(null);
  const savedFingerprintRef = useRef(fingerprint);
  const savingRef = useRef(false);
  const queuedRef = useRef(false);
  const [status, setStatus] = useState<MarkerAutosaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [retryVersion, setRetryVersion] = useState(0);

  latestRef.current = { layoutId, markers, fingerprint };
  saveRef.current = save;
  onErrorRef.current = onError;

  const markSaved = useCallback((savedMarkers: readonly PhotoMarker[]) => {
    savedFingerprintRef.current = getMarkerDraftFingerprint(savedMarkers);
    setLastSavedAt(new Date());
    setStatus('saved');
  }, []);

  const runSave = useCallback(async () => {
    if (savingRef.current) {
      queuedRef.current = true;
      return;
    }

    const snapshot = latestRef.current;
    if (!snapshot.layoutId || snapshot.fingerprint === savedFingerprintRef.current) return;

    savingRef.current = true;
    setStatus('saving');
    let saved = false;
    try {
      await saveRef.current(snapshot.layoutId, [...snapshot.markers]);
      saved = true;
      if (
        sessionRef.current !== snapshot.layoutId ||
        latestRef.current.layoutId !== snapshot.layoutId
      ) {
        return;
      }
      savedFingerprintRef.current = snapshot.fingerprint;
      setLastSavedAt(new Date());
      setStatus(latestRef.current.fingerprint === snapshot.fingerprint ? 'saved' : 'unsaved');
    } catch (error) {
      if (
        sessionRef.current === snapshot.layoutId &&
        latestRef.current.layoutId === snapshot.layoutId
      ) {
        setStatus('error');
        onErrorRef.current?.(error);
      }
    } finally {
      savingRef.current = false;
      const sameSession =
        sessionRef.current === snapshot.layoutId &&
        latestRef.current.layoutId === snapshot.layoutId;
      const hasNewerChanges =
        saved && latestRef.current.fingerprint !== savedFingerprintRef.current;
      if (sameSession && (queuedRef.current || hasNewerChanges)) {
        queuedRef.current = false;
        setRetryVersion((current) => current + 1);
      }
    }
  }, []);

  useEffect(() => {
    const sessionKey = active && layoutId ? layoutId : null;

    if (sessionRef.current !== sessionKey) {
      sessionRef.current = sessionKey;
      savedFingerprintRef.current = fingerprint;
      queuedRef.current = false;
      setStatus('idle');
      setLastSavedAt(null);
      return;
    }

    if (!active) {
      savedFingerprintRef.current = fingerprint;
      setStatus('idle');
      return;
    }

    if (fingerprint === savedFingerprintRef.current) {
      return;
    }

    setStatus((current) => (current === 'saving' ? current : 'unsaved'));
    if (!enabled) return;

    const timeout = window.setTimeout(() => {
      void runSave();
    }, debounceMs);

    return () => window.clearTimeout(timeout);
  }, [active, debounceMs, enabled, fingerprint, layoutId, retryVersion, runSave]);

  return {
    status,
    lastSavedAt,
    markSaved,
  };
};
